import React, {useContext, useEffect, useState} from "react";
import {ToasterContext} from "../../components/shared/Toaster/ToasterContext";
import {useAuth} from "../../hooks/useAuth";
import * as Yup from "yup";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {NotificationType} from "../../constants/global";
import Container from "@mui/material/Container";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/GridLegacy";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import {useQueryUser} from "../../queries/user/hooks/useQueryUser";
import {CircularProgress, InputAdornment} from "@mui/material";
import ConfirmEmail from "../../components/Auth/ConfirmEmail/ConfirmEmail";
import { useMutationUpdateUser } from "../../queries/user/hooks/useMutationUpdateUser";
import { PHARMACY_USERS } from "../../queries/pharmacy/pharmacyTypeDefs";
import { PharmacyContext } from "../../hooks/pharmacyContext";
import { pageTitleSx } from "../../constants/designTokens";

const Profile:React.FunctionComponent = () => {
  const {addToast} = useContext(ToasterContext);
  const [isConfirmed, setIsConfirmed] = useState(false);

  const { pharmacyId }: any = useContext(PharmacyContext);

  const {data, loading, error, refetch} = useQueryUser();

  const validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required('First name Required'),
    lastName: Yup.string()
      .required('Last name Required'),
    email: Yup.string()
      .required('Email is Required')
      .email('Email is invalid'),
    phone: Yup.string()
      .required('Phone number Required')
  });

  const [updateUser, {loading: updateLoading}] = useMutationUpdateUser({
    refetchQueries: [
      {
        query: PHARMACY_USERS,
        variables: {
          where: { Pharmacy: { is: { id: { equals: +pharmacyId } } } },
          orderBy: [ { createdAt: "asc" } ]
        },
        awaitRefetchQueries: true,
      },
    ],
  });

  const { register, handleSubmit, formState: { errors }, reset } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const onSubmit = async (value: any) => {
    if (!data?.me) {
      addToast({ type: NotificationType.Error, title: 'Unable to load your profile.' });
      return;
    }

    const requestData = {
      firstName: {set: value.firstName},
      lastName: {set: value.lastName},
      email: {set: value.email},
      phone: {set: `+1${value.phone}`},
    }
    updateUser({ variables: { data: requestData, where: {id: Number(data.me.id)} } }).then(({data:{updateUser}}: any) => {
      refetch();
      if (updateUser && data.me.email !== updateUser.email) {
        setIsConfirmed(true);
        addToast({ type: NotificationType.Success, title: 'Verification code is sent' });
      } else {
        setIsConfirmed(false);
        addToast({ type: NotificationType.Success, title: 'Your account has been successfully updated.'  });
      }
    }).catch((error: any) => {
      addToast({ type: NotificationType.Error, title: error.message  });
    });
  };

  useEffect(() => {
    if (data) {
      reset({
        firstName: data.me.firstName,
        lastName: data.me.lastName,
        email: data.me.email,
        phone: data.me.phone?.replace('+1', '') || '',
      });
    }
  }, [reset, data]);

  if (loading) {
    return (
      <Box sx={{ minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data?.me) {
    return (
      <Box sx={{ minHeight: 320, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="error">
          Unable to load your profile. Please refresh and try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline/>
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {isConfirmed ? (
          <ConfirmEmail setIsConfirmed={setIsConfirmed} />
        ) : (
        <>
          <Typography component="h1" sx={pageTitleSx}>
            Profile
          </Typography>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{mt: 1}}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
              <TextField
                autoComplete="email"
                label="Email Address"
                id="email"
                fullWidth
                required
                {...register('email')}
                helperText={errors.email?.message?.toString()}
                error={!!errors.email}
              />
              </Grid>
              <Grid item xs={12}>
              <TextField
                label="First Name"
                id="firstName"
                fullWidth
                required
                helperText={errors.firstName?.message?.toString()}
                {...register('firstName')}
                error={!!errors.firstName}
              />
              </Grid>
              <Grid item xs={12}>
              <TextField
                autoComplete="family-name"
                label="Last Name"
                id="lastName"
                fullWidth
                required
                helperText={errors.lastName?.message?.toString()}
                {...register('lastName')}
                error={!!errors.lastName}
              />
              </Grid>
              <Grid item xs={12}>
              <TextField
                autoComplete="phone"
                label="Phone number"
                id="phone"
                fullWidth
                required
                helperText={errors.phone?.message?.toString()}
                {...register('phone')}
                error={!!errors.phone}
                InputProps={{
                  startAdornment: <InputAdornment position="start">+1</InputAdornment>,
                }}
              />
              </Grid>
            </Grid>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{mt: 3, mb: 2}}
            >
              Save
              { updateLoading && <CircularProgress sx={{ml: 1}} size={14} color='inherit' />}
            </Button>
          </Box>
        </>
        )}
      </Box>
    </Container>
  )
}

export default Profile;